import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { browsers } from 'investira.react.lib';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, TextField, Stack, LoadingButton, Typography } from 'investira.react.components';
import { BaseTemplate } from '../../components/templates';
import withResponseHandling from '../../hoc/withResponseHandling';
import withMessage from '../../hoc/withMessage';
import services from '../../services';
import { DecimalTextField } from '../../components/molecules';
import { useSelector } from 'react-redux';
import { useEntidadesContext } from '../../contexts/EntidadesContext';
import { validators } from 'investira.sdk';
const xValidationSchema = Yup.object().shape({
  custodiante_conferencia_dias_delay: Yup.number().required('Atraso é obrigatório').min(1, 'O atraso não pode ser menor que 1').max(39, 'O atraso não pode ser maior que 39').test('soma-delay-janela', 'A soma de atraso + janela não pode ultrapassar 40', function (xDelay) {
    const {
      custodiante_conferencia_dias_janela: xJanela
    } = this.parent;
    if (xDelay !== undefined && xJanela !== undefined && xDelay !== '' && xJanela !== '') {
      return Number(xDelay) + Number(xJanela) <= 40;
    }
    return true;
  }),
  custodiante_conferencia_dias_janela: Yup.number().required('Janela em dias é obrigatória').min(1, 'A janela não pode ser menor que 1').max(39, 'A janela não pode ser maior que 39').test('soma-delay-janela', 'A soma de atraso + janela não pode ultrapassar 40', function (pJanela) {
    const {
      custodiante_conferencia_dias_delay: xDelay
    } = this.parent;
    if (pJanela !== undefined && xDelay !== undefined && pJanela !== '' && xDelay !== '') {
      return Number(pJanela) + Number(xDelay) <= 40;
    }
    return true;
  }),
  custodiante_conferencia_quantidade_tolerancia: Yup.number()
});
const CustodianteConfig = memo(props => {
  const {
    data,
    current
  } = useSelector(store => store.entidade);
  const {
    actions
  } = useEntidadesContext();

  // Recupera configurações da entidade atual
  const xConfig = data.find(xEntidade => {
    return xEntidade.entidade_id === current.default_entidade_id;
  })?.config;
  const xInitialValues = useMemo(() => {
    return {
      custodiante_conferencia_dias_delay: xConfig?.custodiante_conferencia_dias_delay || 1,
      custodiante_conferencia_dias_janela: xConfig?.custodiante_conferencia_dias_janela || 39,
      custodiante_conferencia_quantidade_tolerancia: xConfig?.custodiante_conferencia_quantidade_tolerancia || 0
    };
  }, [xConfig]);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: xInitialValues,
    validationSchema: xValidationSchema,
    onSubmit: (pValues, pActions) => {
      if (browsers.isOnline()) {
        const xReqAttrs = {
          data: {
            config: {
              custodiante_conferencia_dias_delay: pValues.custodiante_conferencia_dias_delay,
              custodiante_conferencia_dias_janela: pValues.custodiante_conferencia_dias_janela,
              custodiante_conferencia_quantidade_tolerancia: pValues.custodiante_conferencia_quantidade_tolerancia
            }
          }
        };
        pActions.setSubmitting(false);
        // Verifica se houve alguma mudança nos valores
        const isDirty = !validators.isEqual(xInitialValues, pValues);

        // Só atualiza se houver mudanças
        if (isDirty) {
          services.entidades.modify(xReqAttrs, _rRes => {
            // Efetua a chamada do serviço para deletar as conferências da entidade
            services.conferenciasCustodiantes.remove({
              entidade_id: current.default_entidade_id
            }, null, rErr => {
              props.responseErrorHandling(rErr);
            });
            props.onMessageSuccess('Configurações Alteradas!');
          }, rErr => {
            props.responseErrorHandling(rErr);
          }, () => {
            actions.readEntidades();
            pActions.setSubmitting(false);
          });
        }
      } else {
        props.offline();
      }
    }
  });
  const handleNumberChange = (pEvent, pFieldName) => {
    const value = pEvent.target.value;
    if (value === '' || /^[0-9]+$/.test(value)) {
      formik.setFieldValue(pFieldName, value === '' ? '' : parseInt(value, 10));
    }
  };
  const handleChange = (pFieldName, pFieldValue) => {
    formik.setFieldValue(pFieldName, pFieldValue, true);
  };
  return <BaseTemplate title="Configurações da Conferência Custodiante" maxWidth="sm">
      <form onSubmit={formik.handleSubmit} noValidate>
        <Stack gap={3}>
          <Stack direction="row" gap={2} flex="1">
            <Typography variant="body1" color="textPrimary">
              Defina o limite máximo de 40 dias, somando atraso e janela.
            </Typography>
          </Stack>
          <Stack direction="row" gap={2} flex="1">
            <TextField id="custodiante_conferencia_dias_delay" label="Atraso (Dias)" name="custodiante_conferencia_dias_delay" type="number" value={formik.values.custodiante_conferencia_dias_delay} variant="outlined" onChange={pEvent => {
            handleNumberChange(pEvent, 'custodiante_conferencia_dias_delay');
          }} error={Boolean(formik.errors.custodiante_conferencia_dias_delay)} helperText={formik.errors.custodiante_conferencia_dias_delay} fullWidth InputLabelProps={{
            shrink: true
          }} inputProps={{
            min: 1,
            step: 1,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            formNoValidate: 'formnovalidate',
            maxLength: 9
          }} />
            <TextField id="custodiante_conferencia_dias_janela" label="Janela (Dias)" name="custodiante_conferencia_dias_janela" type="number" value={formik.values.custodiante_conferencia_dias_janela} variant="outlined" onChange={pEvent => {
            handleNumberChange(pEvent, 'custodiante_conferencia_dias_janela');
          }} error={Boolean(formik.errors.custodiante_conferencia_dias_janela)} helperText={formik.errors.custodiante_conferencia_dias_janela} fullWidth InputLabelProps={{
            shrink: true
          }} inputProps={{
            min: 1,
            step: 1,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            formNoValidate: 'formnovalidate',
            maxLength: 9
          }} />
          </Stack>
          <Stack gap={3}>
            <Stack direction="row" gap={2} flex="1">
              <Typography variant="body1" color="textPrimary">
                Diferenças inferiores ao valor abaixo, serão ignoradas. Ignorar diferenças
                inferiores a quantidade definida abaixo.
              </Typography>
            </Stack>
            <Stack gap={2}>
              <DecimalTextField id="custodiante_conferencia_quantidade_tolerancia" label="Quantidade (Tolerância)" name="custodiante_conferencia_quantidade_tolerancia" value={formik.values.custodiante_conferencia_quantidade_tolerancia}
            // placeholder={"0,000000"}
            variant="outlined" onChange={(_, pValue) => {
              handleChange('custodiante_conferencia_quantidade_tolerancia', pValue);
            }} error={Boolean(formik.errors.custodiante_conferencia_quantidade_tolerancia)} helperText={formik.errors.custodiante_conferencia_quantidade_tolerancia} InputLabelProps={{
              shrink: true
            }} decimal={6} allowNegative={false} InputProps={{
              endAdornment: null,
              fullWidth: true
            }} fullWidth />
            </Stack>
          </Stack>

          <Box sx={{
          my: 1.5,
          py: 1
        }}>
            <LoadingButton variant="contained" type="submit" disabled={formik.isSubmitting || !formik.isValid} loading={formik.isSubmitting} fullWidth>
              Salvar
            </LoadingButton>
          </Box>
        </Stack>
      </form>
    </BaseTemplate>;
});
CustodianteConfig.propTypes = {
  id: PropTypes.string,
  onBack: PropTypes.func,
  onOpenDialog: PropTypes.func,
  offline: PropTypes.func,
  responseErrorHandling: PropTypes.func
};
CustodianteConfig.defaultProps = {
  id: 'change-password'
};
CustodianteConfig.displayName = 'CustodianteConfig';
export default withResponseHandling(withMessage(CustodianteConfig));
